interface GetTemplatesParams {
  categoryIds?: string[];
  visibility?: string;
  isOfficial?: boolean;
  showInCreateDialog?: boolean;
  populateOnSignup?: boolean;
  title?: string;
}

export interface Template {
  id: string;
  title: string;
  description: string;
  createdBy: number;
  visibility: string;
  organizationId: number;
  isOfficial: boolean;
  populateOnSignup: boolean;
  showInCreateDialog: boolean;
  createdAt: string;
  updatedAt: string;
  categories: { id: string; name: string }[];
}

export class TemplateAPI {
  private static readonly BASE_URL = "/api/v1/templates";
  private static readonly ENDPOINTS = {
    createTemplate: "/create",
    getTemplates: "/all",
    getTemplateCategories: "/categories",
    deleteTemplate: "/",
    updateTemplate: "/update",
  } as const;

  private static readonly HEADERS = {
    "Content-Type": "application/json",
  } as const;

  static async createTemplate(params: {
    title: string;
    description: string;
    columnConfig: string;
    visibility: string;
    categoryId: string;
    isOfficial: boolean;
    populateOnSignup: boolean;
    showInCreateDialog: boolean;
  }): Promise<{ id: string }> {
    const response = await fetch(this.BASE_URL + this.ENDPOINTS.createTemplate, {
      method: "POST",
      headers: this.HEADERS,
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error(errorData);
      throw new Error(errorData.message || "Failed to create template");
    }

    return response.json();
  }

  static async getTemplateCategories(): Promise<{ id: string; name: string }[]> {
    const response = await fetch(this.BASE_URL + this.ENDPOINTS.getTemplateCategories);
    return response.json();
  }

  static async getTemplates(params?: GetTemplatesParams): Promise<Template[]> {
    const queryParams = new URLSearchParams();

    if (params?.categoryIds?.length) {
      params.categoryIds.forEach((id) => queryParams.append("categoryIds", id));
    }
    if (params?.visibility) queryParams.append("visibility", params.visibility);
    if (params?.isOfficial !== undefined) queryParams.append("isOfficial", params.isOfficial.toString());
    if (params?.showInCreateDialog !== undefined)
      queryParams.append("showInCreateDialog", params.showInCreateDialog.toString());
    if (params?.populateOnSignup !== undefined)
      queryParams.append("populateOnSignup", params.populateOnSignup.toString());
    if (params?.title) queryParams.append("title", params.title);

    const url = `${this.BASE_URL}${this.ENDPOINTS.getTemplates}${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
    const response = await fetch(url);
    const data = await response.json();
    return data.templates;
  }

  static async updateTemplate(params: {
    id: string;
    title: string;
    description: string;
    visibility: string;
    isOfficial: boolean;
    populateOnSignup: boolean;
    showInCreateDialog: boolean;
  }): Promise<Template> {
    const response = await fetch(this.BASE_URL + this.ENDPOINTS.updateTemplate, {
      method: "PUT",
      headers: this.HEADERS,
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to update template");
    }

    return await response.json();
  }

  static async deleteTemplate(id: string): Promise<{ success: boolean }> {
    const response = await fetch(this.BASE_URL + this.ENDPOINTS.deleteTemplate + id, {
      method: "DELETE",
      headers: this.HEADERS,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to delete template");
    }

    return response.json();
  }
}
